import {
  ActivityFilter,
  TriggerTypeAppModel,
  useGetCatalogV2,
  useGetIdentities,
  useListActivityFilterOptions,
  useListIntegrationsV2,
} from '@api';
import { useMemo, useState } from 'react';

import {
  MaterialIcon,
  SearchFilterFieldSelect,
  SearchFilterFieldSelectOption,
  SearchFilterItem,
  SearchFilterMultipleSelect,
  SearchFilters,
  SearchFilterValueSelect,
  SearchFilterValueSelectOption,
  stringsToOptions,
} from '@components';
import { Button } from '@mui/material';

import { useAppListAccessFlows } from '@AccessFlows/services/accessFlowsQueries';
import { AccessStatusModel } from '@common/constants';
import { useFlagFixed } from '@hooks';
import { Flag, getStringQP, QueryParams, QueryParamsKeys, removeFromQP } from '@utils';
import { useSearchParams } from 'react-router-dom';

interface SelectedFilter {
  key: number;
  field?: ActivityFilter;
  value?: string | string[];
  dateValue?: {
    startDate: string;
    endDate: string;
  };
}

function queryParamsToFilters(qp: URLSearchParams): SelectedFilter[] {
  const filters: SelectedFilter[] = [];
  let key = 0;

  function addFilter(queryParam: QueryParamsKeys, field: ActivityFilter) {
    const value = getStringQP(qp, queryParam);
    if (value) {
      // If the value includes ',' then it's a multiple value filter otherwise a single value
      const isMultipleValue = value.includes(',');
      filters.push({
        key: key++,
        field,
        value: isMultipleValue ? value.split(',') : value,
      });
    }
  }

  addFilter(QueryParams.filterRequestorId, ActivityFilter.RequestorId);
  addFilter(QueryParams.filterIntegrationId, ActivityFilter.IntegrationId);
  addFilter(QueryParams.filterPermission, ActivityFilter.Permission);
  addFilter(QueryParams.filterResource, ActivityFilter.Resource);
  addFilter(QueryParams.filterStatus, ActivityFilter.Status);
  addFilter(QueryParams.filterResourceType, ActivityFilter.ResourceType);
  addFilter(QueryParams.filterTriggerType, ActivityFilter.TriggerType);
  addFilter(QueryParams.filterAccessFlowId, ActivityFilter.AccessFlowId);

  if (!filters.length) filters.push({ key: 0 });
  return filters;
}

function filtersToQueryParams(filters: SelectedFilter[], oldQP: URLSearchParams): URLSearchParams {
  const newQP = removeFromQP(oldQP, [
    QueryParams.filterRequestorId,
    QueryParams.filterIntegrationId,
    QueryParams.filterPermission,
    QueryParams.filterResource,
    QueryParams.filterStatus,
    QueryParams.filterResourceType,
    QueryParams.filterTriggerType,
    QueryParams.filterAccessFlowId,
  ]);

  filters.forEach((f) => {
    if (!f.value || (Array.isArray(f.value) && f.value.length === 0)) return;
    const values = Array.isArray(f.value) ? f.value.join(',') : f.value;

    if (f.field === ActivityFilter.RequestorId) newQP.set(QueryParams.filterRequestorId, values);
    else if (f.field === ActivityFilter.IntegrationId) newQP.set(QueryParams.filterIntegrationId, values);
    else if (f.field === ActivityFilter.Permission) newQP.set(QueryParams.filterPermission, values);
    else if (f.field === ActivityFilter.Resource) newQP.set(QueryParams.filterResource, values);
    else if (f.field === ActivityFilter.Status) newQP.set(QueryParams.filterStatus, values);
    else if (f.field === ActivityFilter.ResourceType) newQP.set(QueryParams.filterResourceType, values);
    else if (f.field === ActivityFilter.TriggerType) newQP.set(QueryParams.filterTriggerType, values);
    else if (f.field === ActivityFilter.AccessFlowId) newQP.set(QueryParams.filterAccessFlowId, values);
  });

  return newQP;
}

function countFilters(qp: URLSearchParams): number {
  let count = 0;

  [
    QueryParams.filterRequestorId,
    QueryParams.filterIntegrationId,
    QueryParams.filterPermission,
    QueryParams.filterResource,
    QueryParams.filterStatus,
    QueryParams.filterResourceType,
    QueryParams.filterTriggerType,
    QueryParams.filterAccessFlowId,
  ].forEach((param) => {
    if (qp.has(param)) count++;
  });

  return count;
}

function useActivityFilters() {
  const [qp, setQp] = useSearchParams();

  const [filters, setFilters] = useState<SelectedFilter[]>(queryParamsToFilters(qp));
  const [popoverOpened, setPopoverOpened] = useState(false);

  function togglePopover() {
    setPopoverOpened((prev) => !prev);
    if (!popoverOpened) setFilters(queryParamsToFilters(qp));
  }

  function closePopover() {
    setPopoverOpened(false);
  }

  const countedFilters = useMemo(() => countFilters(qp), [qp]);

  const fieldOptions: SearchFilterFieldSelectOption<ActivityFilter>[] = useMemo(() => {
    function setOption(key: ActivityFilter, value: string) {
      return {
        key,
        value,
        disabled: !!filters.find((f) => f.field === key),
      };
    }

    return [
      setOption(ActivityFilter.RequestorId, 'Grantee'),
      setOption(ActivityFilter.IntegrationId, 'Integration'),
      setOption(ActivityFilter.Permission, 'Permission'),
      setOption(ActivityFilter.Resource, 'Resource'),
      setOption(ActivityFilter.Status, 'Status'),
      setOption(ActivityFilter.ResourceType, 'Resource Type'),
      setOption(ActivityFilter.TriggerType, 'Trigger Type'),
      setOption(ActivityFilter.AccessFlowId, 'Access Flow Name'),
    ];
  }, [filters]);

  function addFilter() {
    setFilters((prev) => {
      if (prev.length === 0) {
        return [{ key: 0 }];
      }

      const lastFilter = prev[prev.length - 1];
      return [...prev, { key: lastFilter.key + 1 }];
    });
  }

  function deleteFilter(key: number) {
    setFilters((prev) => prev.filter((f) => f.key !== key));
  }

  function updateFilter(key: number, filter: SelectedFilter) {
    setFilters((prev) => prev.map((f) => (f.key === key ? filter : f)));
  }

  function applyFilters(customFilters?: SelectedFilter[]) {
    const newQueryParams = filtersToQueryParams(customFilters || filters, qp);

    if (qp.has(QueryParams.page)) newQueryParams.set(QueryParams.page, '1');

    setQp(newQueryParams);
    closePopover();
  }

  function clearFilters() {
    const lastFilter = filters[filters.length - 1];
    setFilters([{ key: lastFilter.key + 1 }]);
    applyFilters([]);
  }

  return {
    fieldOptions,
    filters,
    countedFilters,
    popover: {
      isOpen: popoverOpened,
      toggleDropdown: togglePopover,
      closeDropdown: closePopover,
    },
    addFilter,
    deleteFilter,
    updateFilter,
    applyFilters,
    clearFilters,
  };
}

export function ActivityFilters() {
  const {
    fieldOptions,
    filters,
    countedFilters,
    popover,
    addFilter,
    updateFilter,
    deleteFilter,
    applyFilters,
    clearFilters,
  } = useActivityFilters();

  return (
    <SearchFilters
      popover={popover}
      trigger={
        <Button
          size="small"
          variant={countedFilters > 0 ? 'contained-filters' : 'outlined-filters'}
          startIcon={<MaterialIcon symbol="filter_list" />}
          endIcon={<MaterialIcon symbol="expand_more" />}
          component="div"
        >
          Filter {countedFilters > 0 ? `/ ${countedFilters}` : ''}
        </Button>
      }
      onNewItemClick={addFilter}
      onApplyClick={applyFilters}
      onClearClick={clearFilters}
    >
      {filters.map((filter, index) => (
        <ActivityFilterItem
          key={filter.key}
          prefix={index === 0 ? 'Where' : 'And'}
          fieldOptions={fieldOptions}
          filter={filter}
          onUpdate={(val) => updateFilter(filter.key, val)}
          onDelete={filters.length > 1 ? () => deleteFilter(filter.key) : undefined}
        />
      ))}
    </SearchFilters>
  );
}

interface ActivityFilterItemProps {
  fieldOptions: SearchFilterFieldSelectOption<ActivityFilter>[];
  filter: SelectedFilter;
  prefix?: string;
  onDelete?: () => void;
  onUpdate: (filter: SelectedFilter) => void;
}

function ActivityFilterItem({ fieldOptions, filter, prefix = 'Where', onDelete, onUpdate }: ActivityFilterItemProps) {
  const handleFieldSelect = (value: ActivityFilter) => {
    onUpdate({
      ...filter,
      field: value,
      value: undefined,
      dateValue: undefined,
    });
  };

  const handleFieldValue = (currentValue?: string[] | string) => {
    onUpdate({ ...filter, value: currentValue });
  };

  function valueSelect() {
    if (!filter.field) return null;

    switch (filter.field) {
      case ActivityFilter.RequestorId:
        return <ActivityFilterRequestorItem value={filter.value} onChange={handleFieldValue} />;
      case ActivityFilter.IntegrationId:
        return <ActivityFilterIntegrationItem value={filter.value} onChange={handleFieldValue} />;
      case ActivityFilter.Resource:
        return <ActivityFilterResourceItem value={filter.value} onChange={handleFieldValue} />;
      case ActivityFilter.Permission:
        return <ActivityFilterPermissionItem value={filter.value} onChange={handleFieldValue} />;
      case ActivityFilter.Status:
        return <ActivityFilterStatusItem value={filter.value} onChange={handleFieldValue} />;
      case ActivityFilter.ResourceType:
        return <ActivityFilterResourceTypeItem value={filter.value} onChange={handleFieldValue} />;
      case ActivityFilter.TriggerType:
        return <ActivityFilterTriggerTypeItem value={filter.value} onChange={handleFieldValue} />;
      case ActivityFilter.AccessFlowId:
        return <ActivityFilterAccessFlowItem value={filter.value} onChange={handleFieldValue} />;
    }
  }

  const operation = () => {
    if (!filter.field) return undefined;
    return <>{'is'}</>;
  };

  return (
    <SearchFilterItem
      prefix={<>{prefix}</>}
      fieldSelect={<SearchFilterFieldSelect options={fieldOptions} value={filter.field} onChange={handleFieldSelect} />}
      operation={operation()}
      onDelete={onDelete}
    >
      {valueSelect()}
    </SearchFilterItem>
  );
}

interface ActivityFilterFieldProps {
  value?: string[] | string;
  onChange: (value?: string[] | string) => void;
}

function ActivityFilterRequestorItem({ value, onChange }: ActivityFilterFieldProps) {
  const { options, isOptionsFetched } = useListActivityFilterOptions(ActivityFilter.RequestorId);
  const {
    identities: { identities },
    isIdentitiesFetched,
  } = useGetIdentities();

  const identityOptions: SearchFilterValueSelectOption[] = useMemo(() => {
    return options.map((id) => {
      const identity = identities.find((i) => i.id === id);
      if (identity) {
        return {
          value: id,
          label: `${identity.first_name} ${identity.last_name} (${identity.email})`,
        };
      }

      return { value: id, label: id };
    });
  }, [options, identities]);

  return (
    <SearchFilterMultipleSelect
      value={value}
      options={identityOptions}
      loading={!isOptionsFetched || !isIdentitiesFetched}
      onChange={onChange}
    />
  );
}

function ActivityFilterResourceTypeItem({ value, onChange }: ActivityFilterFieldProps) {
  const { options, isOptionsFetched } = useListActivityFilterOptions(ActivityFilter.ResourceType);
  const { isIntegrationsConfigsFetched } = useGetCatalogV2();

  const resourceOptions: SearchFilterValueSelectOption[] = useMemo(
    () =>
      options.map((resource) => ({
        value: resource,
        label: resource,
      })),
    [options],
  );

  return (
    <SearchFilterMultipleSelect
      value={value}
      options={resourceOptions}
      loading={!isOptionsFetched || !isIntegrationsConfigsFetched}
      onChange={onChange}
    />
  );
}

function ActivityFilterIntegrationItem({ value, onChange }: ActivityFilterFieldProps) {
  const { options, isOptionsFetched } = useListActivityFilterOptions(ActivityFilter.IntegrationId);
  const { integrations, isIntegrationsFetched } = useListIntegrationsV2();

  const integrationOptions: SearchFilterValueSelectOption[] = useMemo(() => {
    return options.map((id) => {
      const integration = integrations.find((i) => i.id === id);
      if (integration) return { value: id, label: integration.name };
      return { value: id, label: id };
    });
  }, [options, integrations]);

  return (
    <SearchFilterMultipleSelect
      value={value}
      options={integrationOptions}
      loading={!isOptionsFetched || !isIntegrationsFetched}
      onChange={onChange}
    />
  );
}

function ActivityFilterResourceItem({ value, onChange }: ActivityFilterFieldProps) {
  const { options, isOptionsFetched } = useListActivityFilterOptions(ActivityFilter.Resource);
  return (
    <SearchFilterMultipleSelect
      value={value}
      options={stringsToOptions(options)}
      loading={!isOptionsFetched}
      onChange={onChange}
    />
  );
}

function ActivityFilterPermissionItem({ value, onChange }: ActivityFilterFieldProps) {
  const { options, isOptionsFetched } = useListActivityFilterOptions(ActivityFilter.Permission);
  return (
    <SearchFilterMultipleSelect
      value={value}
      options={stringsToOptions(options)}
      loading={!isOptionsFetched}
      onChange={onChange}
    />
  );
}

function ActivityFilterStatusItem({ value, onChange }: ActivityFilterFieldProps) {
  const statusOptions: SearchFilterValueSelectOption[] = [
    { value: AccessStatusModel.Granting, label: AccessStatusModel.Granting },
    { value: AccessStatusModel.Rejected, label: AccessStatusModel.Rejected },
    { value: AccessStatusModel.Active, label: AccessStatusModel.Active },
    { value: AccessStatusModel.Pending, label: AccessStatusModel.Pending },
    { value: AccessStatusModel.Revoking, label: AccessStatusModel.Revoking },
    { value: AccessStatusModel.Revoked, label: AccessStatusModel.Revoked },
    { value: AccessStatusModel.Failed, label: AccessStatusModel.Failed },
  ];
  return <SearchFilterValueSelect value={value as string} options={statusOptions} onChange={onChange} />;
}

function ActivityFilterTriggerTypeItem({ value, onChange }: ActivityFilterFieldProps) {
  const { isEnabled: showAutogrant } = useFlagFixed(Flag.AUTO_GRANT);
  const triggerTypeOptions: SearchFilterValueSelectOption[] = [
    { value: TriggerTypeAppModel.UserRequest, label: 'Self Serve' },
  ];
  if (showAutogrant) triggerTypeOptions.push({ value: TriggerTypeAppModel.AutoGrant, label: 'Automatic' });

  return <SearchFilterMultipleSelect value={value} options={triggerTypeOptions} onChange={onChange} />;
}

function ActivityFilterAccessFlowItem({ value, onChange }: ActivityFilterFieldProps) {
  const { options, isOptionsFetched } = useListActivityFilterOptions(ActivityFilter.AccessFlowId);
  const { accessFlows, isFetched: isAccessFlowsFetched } = useAppListAccessFlows();

  const accessFlowOptions: SearchFilterValueSelectOption[] = useMemo(() => {
    return options.map((id) => {
      const accessFlow = accessFlows.find((ac) => ac.id === id);
      if (accessFlow) return { value: id, label: accessFlow.name };
      return { value: id, label: id };
    });
  }, [options, accessFlows]);

  return (
    <SearchFilterMultipleSelect
      value={value}
      options={accessFlowOptions}
      loading={!isOptionsFetched || !isAccessFlowsFetched}
      onChange={onChange}
    />
  );
}
